const LOGIN_ATTEMPT_TIMEOUT = 10000;
const LOGIN_EXPIRED_INTERVAL = 5000;
const REFRESH_LOGIN_TIMEOUT = 1000 * 60;
import angular from 'angular';
import { shouldRemoveAccountExtrasFromAuthentication } from 'app.constants';
import { onLogout } from '../../../sessionManager/SessionManager';
export const AUTH_COOKIE = 'auth';
class AuthenticationService {
    static $inject = ['$http', '$cookies', '$base64', '$timeout', '$interval', 'TranslateService'];
    constructor($http, $cookies, $base64, $timeout, $interval, TranslateService) {
        'ngInject';
        this.$http = $http;
        this.$cookies = $cookies;
        this.$base64 = $base64;
        this.$timeout = $timeout;
        this.$interval = $interval;
        this.TranslateService = TranslateService;
        this.init();
    }
    async init() {
        this.$http.defaults.headers.common.Authorization = `Bearer ${this.token}`;
        this.createInitializationPromise();
    }
    createInitializationPromise() {
        this.waitForInitializationPromise = {
            resolve: () => undefined,
            reject: () => undefined,
            promise: new Promise(() => undefined),
        };
        const promise = new Promise((resolve, reject) => {
            // @ts-ignore
            this.waitForInitializationPromise.resolve = resolve;
            this.waitForInitializationPromise.reject = reject;
        });
        this.waitForInitializationPromise.promise = promise;
    }
    waitForInitialization() {
        return this.waitForInitializationPromise.promise;
    }
    async login(email, password) {
        if (!this.isAuthenticated() || password) {
            this.$http.defaults.headers.common.Authorization = `Basic ${this.$base64.encode(email + ':' + password)}`;
        }
        this._setLoginTimeouts();
    }
    logout() {
        onLogout();
        this.auth = undefined;
    }
    isAuthenticated() {
        return this.token && this.expires >= new Date();
    }
    setAccount(account) {
        const accountToStore = angular.copy(account);
        if (shouldRemoveAccountExtrasFromAuthentication) {
            // We don't need to store the extras
            delete accountToStore.extras;
        }
        if (accountToStore) {
            this.TranslateService.setCurrentLanguage(accountToStore.culture, navigator.language);
        }
        this.auth = { ...this.auth, account: accountToStore };
    }
    getAuth() {
        return (this.$cookies.getObject(AUTH_COOKIE));
    }
    getAccount() {
        return (this.$cookies.getObject(AUTH_COOKIE) || {}).account;
    }
    isAccountPending() {
        return this.getAccount().isPending;
    }
    //
    set auth(auth) {
        if (auth) {
            this.$cookies.remove(AUTH_COOKIE, { path: '/' });
            this.$cookies.putObject(AUTH_COOKIE, auth, {
                expires: new Date(auth.expires),
                path: '/',
            });
            this.$http.defaults.headers.common.Authorization = `Bearer ${auth.token}`;
            this.waitForInitializationPromise.resolve();
        }
        else {
            this.$cookies.remove(AUTH_COOKIE, { path: '/' });
        }
    }
    get auth() {
        return this.$cookies.getObject(AUTH_COOKIE);
    }
    get email() {
        return (this.$cookies.getObject(AUTH_COOKIE) || {}).email;
    }
    get token() {
        return (this.$cookies.getObject(AUTH_COOKIE) || {}).token;
    }
    get expires() {
        return new Date((this.$cookies.getObject(AUTH_COOKIE) || {}).expires);
    }
    getAuthRedirectUrl(url) {
        const auth = this.$base64.encode(`${this.email}:${this.auth.token}`);
        return `${url}?auth=${auth}`;
    }
    _setLoginTimeouts() {
        if (this.refreshLoginTimeout) {
            this.$timeout.cancel(this.refreshLoginTimeout);
        }
        if (this.loginExpiredInterval) {
            this.$interval.cancel(this.loginExpiredInterval);
        }
        if (this.isAuthenticated()) {
            // automatically retry logging in 1min before token expiration
            const refreshLoginTimeout = this.expires -
                new Date().getTime() -
                REFRESH_LOGIN_TIMEOUT -
                LOGIN_ATTEMPT_TIMEOUT;
            this.refreshLoginTimeout = this.$timeout(() => {
                this._tryReLogin();
            }, refreshLoginTimeout);
            // check for expired token every 5 seconds
            this.loginExpiredInterval = this.$interval(() => {
                if (new Date() > this.expires) {
                    this.$interval.cancel(this.loginExpiredInterval);
                    this.login(this.email, undefined);
                }
            }, LOGIN_EXPIRED_INTERVAL);
        }
    }
    async _tryReLogin() {
        while (true) {
            // eslint-disable-line no-constant-condition
            try {
                await this.$timeout(() => {
                    this.login(this.email, undefined);
                }, LOGIN_ATTEMPT_TIMEOUT); // eslint-disable-line no-await-in-loop
                return true;
            }
            catch (err) {
                continue;
            }
        }
    }
}
// Explicit AngularJS DI annotation for minification safety
// Order must match constructor parameter order
AuthenticationService.$inject = ['$http', '$cookies', '$base64', '$timeout', '$interval', 'TranslateService'];
export default AuthenticationService;
